/**
 * WorkflowVerify - Step 2 of the work order completion workflow.
 *
 * The primary technician reviews all completed checklist items, takes
 * verification photos documenting the finished work, and confirms
 * everything is done. Only the primary contact can access this step.
 *
 * Features:
 * - Summary of completed checklist items (read-only)
 * - Photo capture for verification documentation
 * - Photo preview gallery with remove capability
 * - "Verify & Continue" button to advance to sign-off
 */
import React from 'react';
import { Alert } from '../../atoms';
import type { ChecklistItem } from '../../../services/checklistService';
import type { CapturedPhoto } from '../../organisms/PhotoCapture/PhotoCapture';
import PhotoCapture from '../../organisms/PhotoCapture/PhotoCapture';
import Button from '../../atoms/Button/Button';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface WorkflowVerifyProps {
    /** All checklist items (shown as a read-only summary) */
    items: ChecklistItem[];
    /** Photos captured during verification */
    verificationPhotos: CapturedPhoto[];
    /** Handler for when a new photo is captured */
    onAddPhoto: (photo: CapturedPhoto) => void;
    /** Handler to remove a photo by index */
    onRemovePhoto: (index: number) => void;
    /** Handler to submit verification and advance to sign-off */
    onSubmitVerification: () => Promise<void>;
    /** Handler to go back to the checklist step */
    onBack: () => void;
    /** Whether submission is in progress */
    isSubmitting: boolean;
    /** Error message to display */
    error: string | null;
    /** Whether the device is offline */
    isOffline: boolean;
    /** Optional outer className merged onto the root element. */
    className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

const WorkflowVerify: React.FC<WorkflowVerifyProps> = ({
    items,
    verificationPhotos,
    onAddPhoto,
    onRemovePhoto,
    onSubmitVerification,
    onBack,
    isSubmitting,
    error,
    isOffline,
    className,
}) => {
    return (
        <div className={['workflow-verify', 'flex flex-col h-full', className ?? ''].join(' ')} data-ref="organism-workflow-verify">
            {/* --- Scrollable content area --- */}
            <div className="workflow-verify__body flex-1 overflow-y-auto px-16px pt-16px pb-16px">
                {/* Verification header */}
                <div className="mb-20px">
                    <div className="flex items-center gap-10px mb-8px">
                        {/* Shield check icon */}
                        <div className="w-40px h-40px rounded-full bg-brand-primary/10 flex items-center justify-center">
                            <svg className="w-22px h-22px text-brand-primary" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-18px font-sans font-bold text-brand-text">
                                Verify Completion
                            </h3>
                            <p className="text-13px font-sans text-brand-text-muted">
                                Confirm all work is done and take photos
                            </p>
                        </div>
                    </div>
                </div>

                {/* Offline indicator */}
                {isOffline && (
                    <div className="flex items-center gap-6px mb-12px px-10px py-6px bg-amber-50 border border-amber-200 rounded-md">
                        <svg className="h-14px w-14px text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9.75v6.75m0 0-3-3m3 3 3-3m-8.25 6a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
                        </svg>
                        <span className="text-12px font-sans text-amber-700">
                            Offline — verification will sync when you reconnect
                        </span>
                    </div>
                )}

                {/* Error message */}
                {error && (
                    <Alert variant="error" size="sm" icon className="mb-12px">{error}</Alert>
                )}

                {/* --- Completed items summary --- */}
                <div className="mb-20px">
                    <h4 className="text-13px font-sans font-bold text-brand-text-muted uppercase tracking-wide mb-8px">
                        Completed Items ({items.filter((i) => i.isCompleted).length})
                    </h4>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-12px">
                        <ul className="flex flex-col gap-6px">
                            {/* Only list items the tech actually completed — optional
                                items can be skipped, so showing them with a green check
                                would falsely imply they were done. */}
                            {items.filter((item) => item.isCompleted).map((item) => (
                                <li key={item.id} className="flex items-start gap-8px">
                                    {/* Green checkmark */}
                                    <svg className="w-16px h-16px text-green-600 flex-shrink-0 mt-2px" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                                    </svg>
                                    <span className="text-14px font-sans text-green-800">{item.label}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* --- Photo capture section --- */}
                <div className="mb-20px">
                    <h4 className="text-13px font-sans font-bold text-brand-text-muted uppercase tracking-wide mb-8px">
                        Verification Photos
                    </h4>
                    <p className="text-13px font-sans text-brand-text-muted mb-12px">
                        Take photos to document the completed work. These will be included in the final report.
                    </p>

                    {/* Captured photo previews */}
                    {verificationPhotos.length > 0 && (
                        <div className="grid grid-cols-3 gap-8px mb-12px">
                            {verificationPhotos.map((photo, index) => (
                                <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200">
                                    <img
                                        src={photo.base64}
                                        alt={`Verification ${index + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                    {/* Remove photo button */}
                                    <button
                                        type="button"
                                        onClick={() => onRemovePhoto(index)}
                                        className="absolute top-4px right-4px w-44px h-44px desktop:w-36px desktop:h-36px rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                                        aria-label={`Remove photo ${index + 1}`}
                                    >
                                        <svg className="w-16px h-16px" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Photo capture widget */}
                    <PhotoCapture
                        onCapture={onAddPhoto}
                        label="Add Verification Photo"
                    />
                </div>
            </div>

            {/* --- Bottom action bar --- */}
            <div className="workflow-verify__footer px-16px pb-16px pt-8px border-t border-gray-100 flex flex-col gap-8px">
                <Button
                    variant="primary"
                    size="lg"
                    fullWidth
                    onClick={onSubmitVerification}
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                >
                    Verify & Continue to Sign Off
                </Button>
                <Button
                    variant="ghost"
                    size="md"
                    fullWidth
                    onClick={onBack}
                    disabled={isSubmitting}
                >
                    Back to Checklist
                </Button>
            </div>
        </div>
    );
};

export default WorkflowVerify;
